import { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext();
const THEME_STORAGE_KEY = "realign-theme-mode";

export const ThemeProvider = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        try {
            const saved = localStorage.getItem(THEME_STORAGE_KEY);
            if (saved === "dark") return true;
            if (saved === "light") return false;

            // First-time users: respect OS preference.
            return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
        } catch {
            return false;
        }
    });

    const toggleTheme = () => setIsDarkMode((prev) => !prev);

    useEffect(() => {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, isDarkMode ? "dark" : "light");
        } catch {
            // Ignore persistence errors.
        }
    }, [isDarkMode]);

    const theme = {
        primaryBg: isDarkMode ? "#0F172A" : "#FFFFFF",
        secondaryBg: isDarkMode ? "#1E293B" : "#ECFDF5",
        cardBg: isDarkMode ? "#111827" : "#FFFFFF",
        cardBgTranslucent: isDarkMode ? "rgba(15, 23, 42, 0.78)" : "rgba(255, 255, 255, 0.96)",
        brandGreen: isDarkMode ? "#34D399" : "#059669",
        hoverGreen: isDarkMode ? "#10B981" : "#047857",
        darkGreen: "#064e3b",
        headingText: isDarkMode ? "#F8FAFC" : "#0F172A",
        mutedText: isDarkMode ? "#CBD5E1" : "#475569",
        captionText: isDarkMode ? "#E2E8F0" : "#000000",
        glassBorder: isDarkMode ? "#94A3B82E" : "#FFFFFF80",
        glassBorderStrong: isDarkMode ? "#94A3B847" : "#FFFFFF80",
        haloColor: isDarkMode ? "#34D39914" : "#0596690D",
        cardShadow: isDarkMode ? "0 16px 40px -10px rgba(15, 23, 42, 0.45)" : "0 10px 30px -5px rgba(0,0,0,0.05)",
        halo2: isDarkMode ? "rgba(52, 211, 153, 0.12)" : "rgba(5, 150, 105, 0.15)", // from cardShadow logic in Home.jsx
        borderBottom2: isDarkMode ? "6px solid rgba(52, 211, 153, 0.18)" : "6px solid rgba(5, 150, 105, 0.2)", // from cardBorderBottom
        softBorder2: isDarkMode ? "rgba(148, 163, 184, 0.16)" : "rgba(5, 150, 105, 0.1)", // from softBorder
        illustrationColor1: isDarkMode ? "#4ade80" : "#16a34a",
        illustrationBg1: isDarkMode ? "rgba(22, 163, 74, 0.16)" : "#ecfdf5",
        illustrationColor2: isDarkMode ? "#60a5fa" : "#2563eb",
        illustrationBg2: isDarkMode ? "rgba(37, 99, 235, 0.16)" : "#eef2ff",
        illustrationColor3: isDarkMode ? "#a78bfa" : "#7c3aed",
        illustrationBg3: isDarkMode ? "rgba(124, 58, 237, 0.16)" : "#f5f3ff",
    };

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleTheme, theme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useThemeContext = () => useContext(ThemeContext);
